/**
 * UTILIDADES DE TEXTO
 *
 * Funciones para manipulación y normalización de texto
 * para búsquedas fuzzy, comparaciones insensibles a acentos, etc.
 */

/**
 * Normaliza texto para búsqueda fuzzy eliminando acentos,
 * convirtiendo a minúsculas y eliminando caracteres especiales
 *
 * @param {string} text - Texto a normalizar
 * @returns {string} - Texto normalizado (sin acentos, minúsculas, sin caracteres especiales)
 *
 * @example
 * normalizeTextForSearch("María José Pérez")  // "maria jose perez"
 * normalizeTextForSearch("Ñuñoa")             // "nunoa"
 * normalizeTextForSearch("O'Higgins")         // "o higgins"
 * normalizeTextForSearch("12.345.678-9")      // "12 345 678 9"
 */
/**
 * Parsea un término de búsqueda extrayendo frases entre comillas como un único token.
 * El resto se tokeniza por espacios.
 *
 * @param {string} searchTerm - Término de búsqueda
 * @returns {string[]} Array de tokens
 *
 * @example
 * parseSearchTokens('"fuera de servicio"')       // ['fuera de servicio']
 * parseSearchTokens('motor "fuera de servicio"') // ['motor', 'fuera de servicio']
 * parseSearchTokens('motor hidraulico')          // ['motor', 'hidraulico']
 */
export function parseSearchTokens(searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') return [];
  const tokens = [];
  const regex = /"([^"]+)"|(\S+)/g;
  let match;
  while ((match = regex.exec(searchTerm)) !== null) {
    tokens.push(match[1] || match[2]);
  }
  return tokens;
}

export function normalizeTextForSearch(text) {
  if (text == null || text === '') return '';

  return String(text)
    .toLowerCase()                           // Convertir a minúsculas
    .normalize('NFD')                        // Descomponer caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '')        // Eliminar marcas diacríticas (acentos)
    .replace(/[^a-z0-9\s]/g, ' ')           // Reemplazar caracteres especiales por espacio
    .replace(/\s+/g, ' ')                    // Normalizar espacios múltiples a uno solo
    .trim();                                 // Eliminar espacios al inicio/final
}

/**
 * Verifica si un texto contiene otro (búsqueda fuzzy)
 * Ignora acentos, mayúsculas y caracteres especiales
 *
 * @param {string} text - Texto donde buscar
 * @param {string} searchTerm - Término a buscar
 * @returns {boolean} - true si el término se encuentra en el texto
 *
 * @example
 * fuzzyIncludes("María José", "maria")     // true
 * fuzzyIncludes("Pérez González", "perez") // true
 * fuzzyIncludes("O'Higgins 123", "higgins")// true
 */
export function fuzzyIncludes(text, searchTerm) {
  if (searchTerm == null || searchTerm === '') return true;
  if (text == null) return false;

  const normalizedText = normalizeTextForSearch(text);
  const normalizedSearch = normalizeTextForSearch(searchTerm);

  return normalizedText.includes(normalizedSearch);
}

/**
 * Compara dos textos de forma fuzzy (ignora acentos, mayúsculas, espacios)
 *
 * @param {string} text1 - Primer texto
 * @param {string} text2 - Segundo texto
 * @returns {boolean} - true si los textos son equivalentes
 *
 * @example
 * fuzzyEquals("María", "maria")       // true
 * fuzzyEquals("José", "jose")         // true
 * fuzzyEquals("Pérez", "Perez")       // true
 */
export function fuzzyEquals(text1, text2) {
  return normalizeTextForSearch(text1) === normalizeTextForSearch(text2);
}

/**
 * Elimina solo los acentos de un texto, manteniendo mayúsculas y caracteres especiales
 *
 * @param {string} text - Texto original
 * @returns {string} - Texto sin acentos
 *
 * @example
 * removeAccents("María José Pérez")  // "Maria Jose Perez"
 * removeAccents("Ñuñoa")             // "Nunoa"
 */
export function removeAccents(text) {
  if (text == null || text === '') return '';

  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Verifica si un texto contiene todas las palabras del término de búsqueda (búsqueda tokenizada)
 * Las palabras pueden estar en cualquier orden y no necesitan ser consecutivas.
 * Ignora acentos, mayúsculas y caracteres especiales (combina con búsqueda fuzzy)
 *
 * @param {string} text - Texto donde buscar
 * @param {string} searchTerm - Término a buscar (puede contener múltiples palabras)
 * @returns {boolean} - true si todas las palabras del término se encuentran en el texto
 *
 * @example
 * fuzzyIncludesTokenized("María Paz Moreno González", "maria moreno")     // true
 * fuzzyIncludesTokenized("Juan Carlos Pérez González", "juan perez")      // true
 * fuzzyIncludesTokenized("José Miguel Silva", "miguel jose")              // true (orden no importa)
 * fuzzyIncludesTokenized("Ana López", "ana martinez")                     // false (martinez no está)
 * fuzzyIncludesTokenized("María José", "maria jose perez")                // false (perez no está)
 *
 * @description
 * Esta función implementa búsqueda de palabras no secuenciales:
 * 1. Tokeniza el término de búsqueda (divide por espacios)
 * 2. Normaliza cada token (elimina acentos, minúsculas, caracteres especiales)
 * 3. Verifica que TODAS las palabras estén presentes en el texto
 * 4. El orden de las palabras no importa
 * 5. Las palabras no necesitan ser consecutivas
 */
export function fuzzyIncludesTokenized(text, searchTerm) {
  if (searchTerm == null || searchTerm === '') return true;
  if (text == null) return false;

  // Normalizar texto completo
  const normalizedText = normalizeTextForSearch(text);

  // Tokenizar término de búsqueda respetando frases entre comillas
  const tokens = parseSearchTokens(searchTerm)
    .map(t => normalizeTextForSearch(t))
    .filter(t => t.length > 0);

  // Si no hay tokens válidos después de normalizar, retornar true (búsqueda vacía)
  if (tokens.length === 0) return true;

  // Verificar que TODAS las palabras (tokens) estén presentes en el texto
  // Usar .every() para asegurar que cada token tenga coincidencia
  return tokens.every(token => normalizedText.includes(token));
}

/**
 * Resalta las coincidencias exactas del término de búsqueda en un texto
 * Retorna un array de segmentos de texto, algunos marcados para resaltar
 * SOLO resalta la parte que coincide, no la palabra completa
 *
 * @param {string} text - Texto donde buscar coincidencias
 * @param {string} searchTerm - Término de búsqueda (puede contener múltiples palabras)
 * @returns {Array<{text: string, highlight: boolean}>} - Array de segmentos con flag de resaltado
 *
 * @example
 * highlightMatches("María Paz Moreno", "maria")
 * // [
 * //   { text: "María", highlight: true },
 * //   { text: " Paz Moreno", highlight: false }
 * // ]
 *
 * highlightMatches("Juan Carlos Pérez González", "perez juan")
 * // [
 * //   { text: "Juan", highlight: true },
 * //   { text: " Carlos ", highlight: false },
 * //   { text: "Pérez", highlight: true },
 * //   { text: " González", highlight: false }
 * // ]
 *
 * @description
 * Esta función:
 * 1. Tokeniza el término de búsqueda
 * 2. Busca cada token en el texto normalizado
 * 3. Resalta SOLO la parte que coincide (no la palabra completa)
 * 4. Preserva el texto original (con acentos, mayúsculas)
 */
export function highlightMatches(text, searchTerm) {
  // Si no hay término de búsqueda o texto, retornar texto sin resaltar
  if (!searchTerm || searchTerm.trim() === '' || !text) {
    return [{ text: text || '', highlight: false }];
  }

  // Tokenizar término de búsqueda respetando frases entre comillas
  const tokens = parseSearchTokens(searchTerm)
    .map(t => normalizeTextForSearch(t))
    .filter(t => t.length > 0);

  if (tokens.length === 0) {
    return [{ text, highlight: false }];
  }

  // Normalizar el texto completo para búsqueda
  const normalizedText = normalizeTextForSearch(text);

  // Encontrar todas las posiciones de coincidencias en el texto normalizado
  const matches = [];

  for (const token of tokens) {
    let startIndex = 0;
    while (true) {
      const index = normalizedText.indexOf(token, startIndex);
      if (index === -1) break;

      matches.push({
        start: index,
        end: index + token.length
      });

      startIndex = index + 1; // Buscar la siguiente ocurrencia
    }
  }

  // Si no hay coincidencias, retornar texto sin resaltar
  if (matches.length === 0) {
    return [{ text, highlight: false }];
  }

  // Ordenar y combinar rangos superpuestos
  matches.sort((a, b) => a.start - b.start);
  const mergedMatches = [];
  let current = matches[0];

  for (let i = 1; i < matches.length; i++) {
    const next = matches[i];
    if (next.start <= current.end) {
      // Rangos se superponen o son adyacentes, combinar
      current.end = Math.max(current.end, next.end);
    } else {
      // Rangos separados
      mergedMatches.push(current);
      current = next;
    }
  }
  mergedMatches.push(current);

  // Construir segmentos basados en las posiciones de coincidencias
  const segments = [];
  let lastEnd = 0;

  for (const match of mergedMatches) {
    // Agregar texto antes de la coincidencia (si existe)
    if (match.start > lastEnd) {
      segments.push({
        text: text.substring(lastEnd, match.start),
        highlight: false
      });
    }

    // Agregar texto coincidente
    segments.push({
      text: text.substring(match.start, match.end),
      highlight: true
    });

    lastEnd = match.end;
  }

  // Agregar texto restante después de la última coincidencia
  if (lastEnd < text.length) {
    segments.push({
      text: text.substring(lastEnd),
      highlight: false
    });
  }

  return segments;
}
